'use client';

import { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';

const SearchMetrics = () => {
  const searchTrendsData = useMemo(() => [
    { date: 'Mon', searches: 245, conversions: 32 },
    { date: 'Tue', searches: 312, conversions: 45 },
    { date: 'Wed', searches: 289, conversions: 38 },
    { date: 'Thu', searches: 356, conversions: 52 },
    { date: 'Fri', searches: 423, conversions: 68 },
    { date: 'Sat', searches: 512, conversions: 89 },
    { date: 'Sun', searches: 478, conversions: 76 },
  ], []);

  const popularRoutesData = useMemo(() => [
    { route: 'London-Dubai', searches: 1234 },
    { route: 'London-New York', searches: 1089 },
    { route: 'London-Paris', searches: 967 },
    { route: 'Manchester-Dubai', searches: 845 },
    { route: 'London-Singapore', searches: 723 },
  ], []);

  const cabinClassData = useMemo(() => [
    { name: 'Economy', value: 68, color: '#3B82F6' },
    { name: 'Premium Economy', value: 18, color: '#8B5CF6' },
    { name: 'Business', value: 11, color: '#F59E0B' },
    { name: 'First Class', value: 3, color: '#EF4444' },
  ], []);

  const stats = [
    { label: 'Total Searches', value: '2,615', change: '+12.5%', icon: 'MagnifyingGlassIcon', trend: 'up' },
    { label: 'Avg. Search Time', value: '2.3s', change: '-0.4s', icon: 'ClockIcon', trend: 'up' },
    { label: 'Search Success Rate', value: '94.2%', change: '+2.1%', icon: 'CheckCircleIcon', trend: 'up' },
    { label: 'Unique Users', value: '1,847', change: '+8.3%', icon: 'UserGroupIcon', trend: 'up' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name={stat.icon as any} size={24} className="text-primary" />
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-sm text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search Trends Chart */}
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Search Trends (Last 7 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={searchTrendsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="searches" stroke="#3B82F6" strokeWidth={2} name="Total Searches" />
            <Line type="monotone" dataKey="conversions" stroke="#10B981" strokeWidth={2} name="Conversions" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Routes */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Top Search Routes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={popularRoutesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="route" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="searches" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cabin Class Distribution */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Cabin Class Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={cabinClassData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {cabinClassData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SearchMetrics;